import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Calculator, Sparkles, Loader2, Check, MapPin, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { PropertyFeaturesBlock } from './PropertyFeaturesBlock';
import HelpHint from './HelpHint';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
// Tipos de inmueble que NO tributan por m² estándar: para ellos no se consulta
// el precio m² base ni se calcula valor salida automático; se exige una
// valoración individual (Ajuste manual).
const TIPOS_SIN_PRECIO_M2 = ['local', 'terreno', 'parking'];
const requiresManualValuation = (tipo) => TIPOS_SIN_PRECIO_M2.includes(String(tipo || '').toLowerCase());

// Normalize the asset's tipo_activo field into one of the dropdown values
// (vivienda / casa / pareada / casa_aislada / local / terreno / parking).
// Handles case-insensitive input and common variants (LOCAL_COMERCIAL, "local comercial", etc.).
const DROPDOWN_TIPOS = ['vivienda', 'casa', 'pareada', 'casa_aislada', 'local', 'terreno', 'parking'];
const normalizeTipoActivo = (raw) => {
  const s = String(raw || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (!s) return 'vivienda';
  if (s.includes('local') || s.includes('comercial')) return 'local';
  if (s.includes('terreno') || s.includes('solar')) return 'terreno';
  if (s.includes('parking') || s.includes('garaje') || s.includes('plaza')) return 'parking';
  if (s.includes('pareada') || s.includes('pareado') || s.includes('adosada') || s.includes('adosado')) return 'pareada';
  if (s.includes('casa_aislada') || s.includes('chalet') || s.includes('unifamiliar_aislada') || s.includes('aislada')) return 'casa_aislada';
  if (s.includes('casa') || s.includes('unifamiliar')) return 'casa';
  if (s.includes('vivienda') || s.includes('piso') || s.includes('apartamento') || s.includes('atico') || s.includes('duplex') || s.includes('estudio')) return 'vivienda';
  if (DROPDOWN_TIPOS.includes(s)) return s;
  return 'vivienda';
};
// Full-precision formatter (matches Balance/PDF so all values stay coherent).
const cur = (v) => v != null && v > 0 ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(v) : '-';

const SELECT_OPTIONS = {
  estado_inmueble: [
    { value: '', label: 'Sin definir' },
    { value: 'reformado', label: 'Reformado' },
    { value: 'buen_estado', label: 'Buen estado' },
    { value: 'habitable', label: 'Habitable' },
    { value: 'a_reformar', label: 'A reformar' },
    { value: 'reforma_integral', label: 'Reforma integral' },
  ],
  planta: [
    { value: '', label: 'Sin definir' },
    { value: 'bajo', label: 'Bajo' },
    { value: 'primero', label: 'Primero' },
    { value: 'segundo', label: 'Segundo' },
    { value: 'tercero', label: 'Tercero' },
    { value: 'cuarto', label: 'Cuarto' },
    { value: 'atico', label: 'Atico' },
  ],
  exterior_luminosidad: [
    { value: '', label: 'Sin definir' },
    { value: 'exterior_luminoso', label: 'Exterior luminoso' },
    { value: 'exterior_normal', label: 'Exterior normal' },
    { value: 'interior', label: 'Interior' },
    { value: 'muy_oscuro', label: 'Muy oscuro' },
  ],
  orientacion: [
    { value: '', label: 'Sin definir' },
    { value: 'sur', label: 'Sur' },
    { value: 'este', label: 'Este' },
    { value: 'oeste', label: 'Oeste' },
    { value: 'norte', label: 'Norte' },
    { value: 'doble_orientacion', label: 'Doble orientacion' },
    { value: 'no_consta', label: 'No consta' },
  ],
  posesion_val: [
    { value: '', label: 'Sin definir' },
    { value: 'con_posesion', label: 'Con posesion' },
    { value: 'vacio', label: 'Vacio' },
    { value: 'sin_posesion', label: 'Sin posesion' },
    { value: 'no_consta', label: 'No consta' },
  ],
  vistas_ubicacion_micro: [
    { value: '', label: 'Sin definir' },
    { value: 'vistas_buenas', label: 'Buenas vistas / Buena zona' },
    { value: 'zona_normal', label: 'Zona normal' },
    { value: 'zona_regular', label: 'Zona regular' },
    { value: 'zona_mala', label: 'Zona mala' },
  ],
  distribucion: [
    { value: '', label: 'Sin definir' },
    { value: 'distribucion_muy_buena', label: 'Muy buena' },
    { value: 'distribucion_correcta', label: 'Correcta' },
    { value: 'distribucion_mala', label: 'Mala' },
  ],
};

const CHECKS = [
  { key: 'tiene_terraza', label: 'Terraza' },
  { key: 'tiene_balcon', label: 'Balcon' },
  { key: 'tiene_jardin', label: 'Jardin' },
  { key: 'tiene_piscina', label: 'Piscina' },
  { key: 'tiene_garaje', label: 'Garaje' },
  { key: 'tiene_trastero', label: 'Trastero' },
];

// Reverse map: al cambiar una feature común, sincronizamos el form legacy
// para que el motor de cálculo actual siga consumiendo los mismos valores.
// UNKNOWN / NOT_APPLICABLE → '' (sin ajuste — comportamiento neutro).
const FLOOR_TO_LEGACY = {
  GROUND: 'bajo', FIRST: 'primero', SECOND: 'segundo', THIRD: 'tercero',
  FOURTH: 'cuarto', FIFTH_OR_HIGHER: 'atico',
};
const CONDITION_TO_LEGACY = {
  READY: 'buen_estado',
  LIGHT_RENOVATION: 'a_reformar',
  FULL_RENOVATION: 'reforma_integral',
};

const NIVEL_BADGE = {
  barrio:    'bg-emerald-100 text-emerald-700 border border-emerald-200',
  municipio: 'bg-amber-100   text-amber-800   border border-amber-200',
  provincia: 'bg-red-100     text-red-700     border border-red-200',
  manual:    'bg-purple-100  text-purple-700  border border-purple-200',
};

// Normalize string for matching: lowercase, strip accents, trim
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();

// Catalan/Valencian spelling variants: normalize a word ending in "y" to "i".
// Makes "Alcoy" and "Alcoi" comparable (same municipality).
const normSpellingVariant = (s) => norm(s).replace(/y\b/g, 'i');

// Short Galician / Catalan articles that may prefix a place name
const SHORT_ARTICLES = new Set(['la', 'el', 'a', 'o', 'les', 'els', "l'", 'les', 'lo']);

// Find the canonical value in `list` that matches `raw` (case/accent-insensitive).
// Handles:
//   - "Alicante" matching "Alicante/Alacant"
//   - "A Coruña" matching "CORUÑA" (and vice versa) — via short-article prefix rule
//   - "Alcoy" matching "Alcoi" — via Catalan/Valencian y↔i final heuristic
// IMPORTANT: long prefixes like "Muro de" are NOT accepted — that caused
// "Alcoy" to wrongly match "Muro de Alcoy".
const matchCanonical = (raw, list) => {
  if (!raw || !list?.length) return '';
  const r = norm(raw);
  if (!r) return '';
  const rv = normSpellingVariant(raw);

  // 1. Exact (normalized) match
  let hit = list.find(x => norm(x) === r);
  if (hit) return hit;

  // 1b. Spelling-variant match (Alcoy ↔ Alcoi)
  hit = list.find(x => normSpellingVariant(x) === rv);
  if (hit) return hit;

  // 2. Match before "/" (Alicante vs Alicante/Alacant)
  hit = list.find(x => norm(x.split('/')[0]) === r);
  if (hit) return hit;
  hit = list.find(x => normSpellingVariant(x.split('/')[0]) === rv);
  if (hit) return hit;

  // 3. Asset has "La Coruña" / "A Coruña" → pricedb has "Coruña"
  //    Only strip a SHORT known article; never accept arbitrary prefix words.
  const rParts = r.split(' ');
  if (rParts.length >= 2 && SHORT_ARTICLES.has(rParts[0])) {
    const rCore = rParts.slice(1).join(' ');
    hit = list.find(x => norm(x) === rCore || normSpellingVariant(x) === normSpellingVariant(rCore));
    if (hit) return hit;
  }

  // 4. Pricedb has "La Coruña" / "A Coruña" → asset has "Coruña"
  hit = list.find(x => {
    const xn = norm(x);
    const xParts = xn.split(' ');
    if (xParts.length < 2) return false;
    if (!SHORT_ARTICLES.has(xParts[0])) return false;
    const xCore = xParts.slice(1).join(' ');
    return xCore === r || normSpellingVariant(x.split(' ').slice(1).join(' ')) === rv;
  });
  if (hit) return hit;

  return '';
};

const ValoracionSalidaTab = ({ asset, assetId, onAssetUpdated, readOnly }) => {
  // --- Price base state ---
  const [provincias, setProvincias] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [selProvincia, setSelProvincia] = useState('');
  const [selMunicipio, setSelMunicipio] = useState('');
  const [selZona, setSelZona] = useState('');
  const [selTipo, setSelTipo] = useState(() => normalizeTipoActivo(asset?.tipo_activo));
  const [precioBase, setPrecioBase] = useState(null);
  const [nivelFuente, setNivelFuente] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  // --- Valor 2 (Fotocasa) state ---
  const [precioBaseFotocasa, setPrecioBaseFotocasa] = useState(null);
  const [nivelFuenteFotocasa, setNivelFuenteFotocasa] = useState('');
  const [lookupLoadingFotocasa, setLookupLoadingFotocasa] = useState(false);
  // Fuente elegida para Nivel 2 (individualizada) — 'idealista' | 'fotocasa' | 'media'
  const [sourceChoice, setSourceChoice] = useState('idealista');

  // --- Valuation form state ---
  const [form, setForm] = useState({
    metros: 0, precio_m2_zona: 0, estado_inmueble: '', ascensor: 'no', planta: '',
    exterior_luminosidad: '', orientacion: '', posesion_val: '', vistas_ubicacion_micro: '',
    distribucion: '', tiene_terraza: false, tiene_balcon: false, tiene_jardin: false,
    tiene_piscina: false, tiene_garaje: false, tiene_trastero: false, municipio: '', tipo_activo: '',
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [errors, setErrors] = useState({});
  const [aiReview, setAiReview] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingValorBase, setSavingValorBase] = useState(false);
  const [history, setHistory] = useState([]);
  // Optional manual override of the final Valor Salida. Empty string = no override (use calculation).
  const [manualOverride, setManualOverride] = useState('');
  // Guard: once an individual valuation has been loaded/saved, do NOT let auto-lookup
  // overwrite form.precio_m2_zona. The user can still click "restaurar" to snap to base.
  const hasIndividualRef = useRef(false);
  // Guard: avoid the initial provincia-only lookup flicker before municipio is matched.
  const locReadyRef = useRef(false);

  // Load provincias and tipos
  useEffect(() => {
    axios.get(`${API}/price-m2-base/provincias`, { withCredentials: true })
      .then(r => setProvincias(r.data)).catch(() => {});
    axios.get(`${API}/price-m2-base/tipos`, { withCredentials: true })
      .then(r => setTipos(r.data)).catch(() => {});
  }, []);

  // Init from asset (non-location fields and valuation form)
  useEffect(() => {
    if (!asset) return;
    const saved = asset.valoracion_salida || {};
    const savedResult = asset.valoracion_resultado || null;
    // Mark hasIndividual BEFORE any lookup effects may run: they'll read the ref.
    hasIndividualRef.current = !!savedResult;
    setSelTipo(asset.tipo_activo || 'vivienda');
    setPrecioBase(asset.precio_m2_idealista != null ? asset.precio_m2_idealista : (asset.precio_m2_base || null));
    setNivelFuente(asset.nivel_fuente_idealista || asset.nivel_fuente_precio_base || '');
    setPrecioBaseFotocasa(asset.precio_m2_fotocasa != null ? asset.precio_m2_fotocasa : null);
    setNivelFuenteFotocasa(asset.nivel_fuente_fotocasa || '');
    // Fuente elegida: persistida en valoracion_salida.source_choice.
    // Default (2B): 'media' si existen ambos, si no la que exista.
    const savedChoice = saved.source_choice;
    if (savedChoice && ['idealista', 'fotocasa', 'media'].includes(savedChoice)) {
      setSourceChoice(savedChoice);
    } else {
      const hasV1 = (asset.precio_m2_idealista != null && asset.precio_m2_idealista > 0);
      const hasV2 = (asset.precio_m2_fotocasa != null && asset.precio_m2_fotocasa > 0);
      if (hasV1 && hasV2) setSourceChoice('media');
      else if (hasV2) setSourceChoice('fotocasa');
      else if (hasV1) setSourceChoice('idealista');
      else setSourceChoice('idealista');
    }
    setForm({
      metros: saved.metros || asset.metros || 0,
      precio_m2_zona: saved.precio_m2_zona || asset.precio_m2_base || 0,
      estado_inmueble: saved.estado_inmueble || '',
      ascensor: saved.ascensor || 'no',
      planta: saved.planta || '',
      exterior_luminosidad: saved.exterior_luminosidad || '',
      orientacion: saved.orientacion || '',
      posesion_val: saved.posesion_val || '',
      vistas_ubicacion_micro: saved.vistas_ubicacion_micro || '',
      distribucion: saved.distribucion || '',
      tiene_terraza: saved.tiene_terraza || false,
      tiene_balcon: saved.tiene_balcon || false,
      tiene_jardin: saved.tiene_jardin || false,
      tiene_piscina: saved.tiene_piscina || false,
      tiene_garaje: saved.tiene_garaje || false,
      tiene_trastero: saved.tiene_trastero || false,
      municipio: asset.municipio || '',
      tipo_activo: asset.tipo_activo || '',
    });
    if (savedResult) {
      setResult(savedResult);
      // Restore AI review if it was applied in the saved valoration
      if (savedResult.ai_review) {
        setAiReview(savedResult.ai_review);
        setAiApplied((savedResult.ai_aplicada_pct || 0) !== 0);
      }
      // If the stored individual value diverges from the auto-calculation, treat it as a
      // user-applied manual override so the input shows the value they saved.
      const calc = (saved.metros || asset.metros || 0) * (saved.precio_m2_zona || asset.precio_m2_base || 0);
      const storedFinal = savedResult.valor_salida_base;
      if (storedFinal && calc > 0 && Math.abs(storedFinal - calc * (1 + (savedResult.ajuste_total_pct || 0) / 100)) > 1) {
        setManualOverride(Math.round(storedFinal).toString());
      }
    }
  }, [asset]);

  // Write-through al form legacy cuando se guarda una feature común desde
  // el bloque compartido. Mantiene sincronizado el motor de cálculo actual.
  const handleFeatureSaved = (key, value) => {
    setForm(prev => {
      const upd = { ...prev };
      if (key === 'floor') upd.planta = FLOOR_TO_LEGACY[value] || '';
      else if (key === 'elevator') upd.ascensor = value === 'YES' ? 'si' : 'no';
      else if (key === 'pool') upd.tiene_piscina = value === 'YES';
      else if (key === 'garage') upd.tiene_garaje = value === 'YES';
      else if (key === 'condition') upd.estado_inmueble = CONDITION_TO_LEGACY[value] || '';
      return upd;
    });
  };

  // Match provincia against canonical list (normalize case/accents) when both are ready
  useEffect(() => {
    if (!asset || !provincias.length) return;
    const canonical = matchCanonical(asset.provincia, provincias);
    setSelProvincia(canonical || asset.provincia || '');
  }, [asset, provincias]);

  // Match municipio once its list loads
  useEffect(() => {
    if (!asset || !municipios.length) return;
    const canonical = matchCanonical(asset.municipio, municipios);
    setSelMunicipio(canonical || asset.municipio || '');
  }, [asset, municipios]);

  // Match zona/barrio once its list loads
  useEffect(() => {
    if (!asset) return;
    if (!zonas.length) { setSelZona(asset.zona_barrio || ''); return; }
    const canonical = matchCanonical(asset.zona_barrio, zonas);
    setSelZona(canonical || asset.zona_barrio || '');
  }, [asset, zonas]);

  // Sync dropdown with the asset's tipo_activo whenever it changes (e.g. after
  // onAssetUpdated triggers a parent refetch). Keeps the UI aligned with the BD.
  useEffect(() => {
    setSelTipo(normalizeTipoActivo(asset?.tipo_activo));
  }, [asset?.tipo_activo]);

  // Persist zona_barrio explicitly when the user changes it manually (see onChange
  // handler of the zona select). This avoids the useEffect-based auto-save that
  // was causing loops when `onAssetUpdated` triggered parent re-renders and the
  // new `asset` reference re-fired the effect with a stale `selZona`.
  const persistZona = useCallback(async (next) => {
    if (!asset || !assetId || readOnly) return;
    const current = (asset.zona_barrio || '').trim();
    const clean = (next || '').trim();
    if (current === clean) return;
    try {
      await axios.put(`${API}/assets/${assetId}`, { zona_barrio: clean || null }, { withCredentials: true });
      toast.success(clean ? `Zona guardada: ${clean}` : 'Zona borrada del activo');
      if (onAssetUpdated) onAssetUpdated();
    } catch (err) {
      toast.error('Error al guardar zona: ' + (err.response?.data?.detail || err.message));
    }
  }, [asset, assetId, readOnly, onAssetUpdated]);

  // Load municipios when provincia changes
  useEffect(() => {
    if (!selProvincia) { setMunicipios([]); return; }
    axios.get(`${API}/price-m2-base/municipios?provincia=${encodeURIComponent(selProvincia)}`, { withCredentials: true })
      .then(r => setMunicipios(r.data)).catch(() => {});
  }, [selProvincia]);

  // Load zonas when municipio changes
  useEffect(() => {
    if (!selMunicipio || !selProvincia) { setZonas([]); return; }
    axios.get(`${API}/price-m2-base/zonas?provincia=${encodeURIComponent(selProvincia)}&municipio=${encodeURIComponent(selMunicipio)}`, { withCredentials: true })
      .then(r => setZonas(r.data)).catch(() => {});
  }, [selProvincia, selMunicipio]);

  // Auto-lookup price when location changes — fetch V1 (Idealista) y V2 (Fotocasa) en paralelo.
  const doLookup = useCallback(async (prov, mun, zona, tipo) => {
    if (!prov) return;
    // Skip lookup for tipos without €/m² standard (local, terreno, parking) — these
    // require an individual valuation and should not show an auto base.
    if (requiresManualValuation(tipo)) {
      setPrecioBase(null); setNivelFuente('');
      setPrecioBaseFotocasa(null); setNivelFuenteFotocasa('');
      return;
    }
    setLookupLoading(true); setLookupLoadingFotocasa(true);
    const params = new URLSearchParams({ provincia: prov, tipo_inmueble: tipo || 'vivienda' });
    if (mun) params.set('municipio', mun);
    if (zona) params.set('zona_barrio', zona);
    try {
      const [r1, r2] = await Promise.allSettled([
        axios.get(`${API}/price-m2-base/lookup?${params}`, { withCredentials: true }),
        axios.get(`${API}/market-prices/lookup?${params}`, { withCredentials: true }),
      ]);
      // V1 (Idealista)
      let v1 = null;
      if (r1.status === 'fulfilled') {
        v1 = r1.value.data;
        setPrecioBase(v1.precio_m2);
        setNivelFuente(v1.nivel_fuente || '');
      }
      // V2 (Fotocasa)
      let v2 = null;
      if (r2.status === 'fulfilled') {
        v2 = r2.value.data;
        setPrecioBaseFotocasa(v2.precio_m2);
        setNivelFuenteFotocasa(v2.nivel_fuente || '');
      }
      // Sync precio_m2_zona con la fuente elegida (solo si no hay individual)
      if (!hasIndividualRef.current) {
        const v1p = v1?.precio_m2 || 0;
        const v2p = v2?.precio_m2 || 0;
        let effective = 0;
        if (sourceChoice === 'idealista') effective = v1p;
        else if (sourceChoice === 'fotocasa') effective = v2p;
        else if (sourceChoice === 'media' && v1p && v2p) effective = (v1p + v2p) / 2;
        else effective = v2p || v1p;
        if (effective) setForm(prev => ({ ...prev, precio_m2_zona: effective }));
      }
    } catch {} finally {
      setLookupLoading(false); setLookupLoadingFotocasa(false);
    }
  }, [sourceChoice]);

  useEffect(() => {
    if (!selProvincia) return;
    // Avoid the provincia-only flicker on initial load: wait until municipio matching is resolved.
    // If the asset has a municipio but we haven't matched it yet (municipios list empty or
    // selMunicipio not yet set), defer the lookup.
    if (!locReadyRef.current) {
      const assetMun = (asset?.municipio || '').trim();
      const needsMun = !!assetMun;
      const munResolved = !needsMun || !!selMunicipio;
      if (!munResolved) return;
      locReadyRef.current = true;
    }
    doLookup(selProvincia, selMunicipio, selZona, selTipo);
  }, [selProvincia, selMunicipio, selZona, selTipo, doLookup, asset]);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
  };

  // Persists the current valor_salida_base (after a price table update) and
  // triggers a backend recomputation of roi_rapido + scoring. Useful when the
  // user has just added/updated the €/m² for the asset's municipio/zona in
  // Parámetros · Base de Precios and wants the change reflected in Base de
  // Activos without having to press "Recalcular scores" globally.
  const handleSaveValorBase = async () => {
    if (!assetId) return;
    setSavingValorBase(true);
    try {
      const r = await axios.post(`${API}/assets/${assetId}/refresh-valor-base`,
        { source_choice: sourceChoice },
        { withCredentials: true });
      const v = r.data?.valor_salida_base;
      const roi = r.data?.roi_rapido;
      toast.success(
        `Valor base guardado: ${v ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '—'}` +
        (roi != null ? ` · ROI ${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(roi)}%` : '')
      );
      // Refresh parent's asset cache so the rest of the UI (ficha, ROI, scoring) picks up the change
      if (typeof onAssetUpdated === 'function') await onAssetUpdated();
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSavingValorBase(false);
    }
  };

  const handleCalculate = async () => {
    const missing = {};
    if (!form.metros) missing.metros = 'Metros (m2)';
    if (!form.precio_m2_zona) missing.precio_m2_zona = 'Precio/m2';
    if (Object.keys(missing).length > 0) {
      setErrors(missing);
      toast.error('Campos obligatorios: ' + Object.values(missing).join(', '));
      return;
    }
    setErrors({});
    setCalculating(true);
    setAiReview(null);
    try {
      // El payload conserva metros/precio y las 5 features objetivas, pero
      // anula los ajustes subjetivos ocultos (luminosidad, orientación,
      // posesión, vistas, distribución, extras) para que dejen de impactar
      // los nuevos cálculos, tal y como pide la nueva arquitectura unificada.
      const payload = {
        ...form,
        exterior_luminosidad: '',
        orientacion: '',
        posesion_val: '',
        vistas_ubicacion_micro: '',
        distribucion: '',
        tiene_terraza: false,
        tiene_balcon: false,
        tiene_jardin: false,
        tiene_trastero: false,
      };
      const res = await axios.post(`${API}/assets/${assetId}/calculate-valuation`, payload, { withCredentials: true });
      setResult(res.data);
      if (onAssetUpdated) onAssetUpdated();
      toast.success('Valoracion individual calculada y guardada');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setCalculating(false); }
  };

  const handleAiReview = async () => {
    setAiLoading(true);
    setAiApplied(false);
    try {
      const payload = {
        ...form,
        exterior_luminosidad: '',
        orientacion: '',
        posesion_val: '',
        vistas_ubicacion_micro: '',
        distribucion: '',
        tiene_terraza: false,
        tiene_balcon: false,
        tiene_jardin: false,
        tiene_trastero: false,
      };
      const res = await axios.post(`${API}/assets/${assetId}/valuation-ai-review`, payload, { withCredentials: true });
      setAiReview(res.data);
    } catch { toast.error('Error al consultar IA'); }
    finally { setAiLoading(false); }
  };

  // Apply the AI correction to the current result: valor_salida_base += correction
  const applyAiCorrection = () => {
    if (!aiReview || !result) return;
    const corrected = Math.round(aiReview.valor_con_correccion || result.valor_salida_base);
    setResult(r => ({
      ...r,
      valor_salida_base: corrected,
      ai_aplicada_pct: aiReview.correccion_pct,
    }));
    setAiApplied(true);
    toast.success(`Corrección aplicada: ${aiReview.correccion_pct >= 0 ? '+' : ''}${aiReview.correccion_pct}%`);
  };

  const handleSaveValoracion = async () => {
    if (!result || !result.valor_salida_base) {
      toast.error('Calcula primero la valoración individual');
      return;
    }
    const finalValue = (manualOverride !== '' && manualOverride !== null && !isNaN(parseFloat(manualOverride)))
      ? parseFloat(manualOverride)
      : result.valor_salida_base;
    setSaving(true);
    try {
      const payload = {
        valor_base: result.valor_base || valorBase,
        valor_individual: finalValue,
        valor_final: finalValue,
        ajuste_total_pct: result.ajuste_total_pct,
        correccion_ia_pct: aiApplied ? (aiReview?.correccion_pct || 0) : 0,
        ajustes: result.ajustes || [],
        ai_review: aiReview || null,
        manual_override: (manualOverride !== '' && manualOverride !== null) ? parseFloat(manualOverride) : null,
      };
      await axios.post(`${API}/assets/${assetId}/save-valoracion-salida`, payload, { withCredentials: true });
      toast.success('Valoración guardada en el histórico');
      await loadHistory();
      if (onAssetUpdated) onAssetUpdated();
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally { setSaving(false); }
  };

  const loadHistory = useCallback(async () => {
    if (!assetId) return;
    try {
      const res = await axios.get(`${API}/assets/${assetId}/valoracion-salida-history`, { withCredentials: true });
      setHistory(res.data.items ?? res.data ?? []);
    } catch { /* ignore */ }
  }, [assetId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Standalone manual valuation: lets the user set the Valor de Salida directly
  // without going through the full Niveles 1+2 flow. Especially useful for
  // CREDITO assets or any case where the user already knows the figure and
  // doesn't want (or can't) populate the granular adjustments.
  const [valorSalidaManual, setValorSalidaManual] = useState('');
  const [savingManual, setSavingManual] = useState(false);

  useEffect(() => {
    if (!asset) return;
    // Pre-fill with the current valor_salida_individual (the explicit user
    // override) if present, otherwise leave it empty so the placeholder shows
    // the auto-calculated one.
    const v = asset.valor_salida_individual;
    setValorSalidaManual(v && v > 0 ? Math.round(v).toString() : '');
  }, [asset]);

  const handleSaveValorSalidaManual = async () => {
    const n = parseFloat(valorSalidaManual);
    if (!n || n <= 0) { toast.error('Introduce un valor mayor que 0'); return; }
    setSavingManual(true);
    try {
      await axios.post(
        `${API}/assets/${assetId}/save-valoracion-salida`,
        {
          valor_base: valorBase || 0,
          valor_individual: n,
          valor_final: n,
          manual_override: n,
          ajuste_total_pct: 0,
          correccion_ia_pct: 0,
          ajustes: [],
          ai_review: null,
          notas: 'Ajuste manual directo (sin pasar por cálculo automático)',
        },
        { withCredentials: true },
      );
      toast.success(`Valor de salida guardado: ${cur(n)}`);
      await loadHistory();
      if (onAssetUpdated) onAssetUpdated();
    } catch (err) {
      toast.error('Error al guardar: ' + (err.response?.data?.detail || err.message));
    } finally { setSavingManual(false); }
  };

  const metros = form.metros || 0;
  const valorBase = metros * (precioBase || 0);
  const valorIndividual = result ? result.valor_salida_base : null;

  return (
    <div className="space-y-6" data-testid="valoracion-salida-tab">

      {/* ===== NIVEL 1: VALOR BASE (from price table) ===== */}
      <div className="card-base p-6">
        <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
          <h3 className="heading-3 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Nivel 1: Valor Base (tabla de precios)
          </h3>
          <button
            type="button"
            onClick={() => {
              // Copia la REFERENCIA CATASTRAL al portapapeles (como pidió el
              // usuario, no la dirección) y abre Idealista Maps en popup.
              const rc = asset?.referencia_catastral || '';
              const open = () => {
                const w = 1280, h = 820;
                const left = Math.max(0, Math.floor((window.screen.availWidth - w) / 2));
                const top = Math.max(0, Math.floor((window.screen.availHeight - h) / 2));
                const popup = window.open(
                  'https://www.idealista.com/maps/',
                  'idealista_maps',
                  `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes,popup=yes`,
                );
                if (!popup) toast.error('El navegador bloqueó el popup. Permite ventanas emergentes para esta web.');
              };
              if (rc && navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(rc)
                  .then(() => toast.success(`RC copiada: ${rc} — pega en Idealista (Ctrl+V)`))
                  .catch(() => { /* fallback: just open */ })
                  .finally(open);
              } else {
                open();
              }
            }}
            disabled={!asset?.referencia_catastral}
            className="text-xs font-medium text-slate-700 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 border border-slate-200 rounded-md px-2.5 py-1.5 hover:bg-slate-50 transition-colors"
            title={asset?.referencia_catastral
              ? 'Copia la RC al portapapeles y abre Idealista Maps en una ventana sobre la app'
              : 'Necesita referencia catastral'}
            data-testid="open-idealista-maps"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Idealista Maps
          </button>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-5">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Provincia<HelpHint text="Provincia donde se ubica el inmueble. Determina el rango de precios/m² de referencia usados para la valoración." /></label>
            <select className="input-base text-sm" value={selProvincia} onChange={e => { setSelProvincia(e.target.value); setSelMunicipio(''); setSelZona(''); }} disabled={readOnly} data-testid="val-provincia">
              <option value="">Seleccionar...</option>
              {provincias.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <ComboField
            label="Municipio"
            help="Municipio del inmueble. Aplica los comparables de precio/m² recogidos de Fotocasa para ese municipio."
            value={selMunicipio}
            options={municipios}
            onChange={v => { setSelMunicipio(v); setSelZona(''); }}
            onAdd={async (v) => {
              try {
                await axios.post(`${API}/price-m2-base/add-entry`, { provincia: selProvincia, municipio: v, tipo_inmueble: selTipo }, { withCredentials: true });
                const r = await axios.get(`${API}/price-m2-base/municipios?provincia=${encodeURIComponent(selProvincia)}`, { withCredentials: true });
                setMunicipios(r.data);
                setSelMunicipio(v);
                toast.success(`Municipio "${v}" añadido`);
              } catch { toast.error('Error al añadir'); }
            }}
            disabled={readOnly || !selProvincia}
            placeholder="Buscar o escribir municipio..."
            tid="val-municipio"
          />
          <ComboField
            label="Zona / Barrio"
            help="Sub-área del municipio. Si tiene comparables propios, se usan para afinar la valoración; si no, se usa el precio del municipio."
            value={selZona}
            options={zonas}
            onChange={v => { setSelZona(v); persistZona(v); }}
            onAdd={async (v) => {
              try {
                await axios.post(`${API}/price-m2-base/add-entry`, { provincia: selProvincia, municipio: selMunicipio, zona_barrio: v, tipo_inmueble: selTipo }, { withCredentials: true });
                const r = await axios.get(`${API}/price-m2-base/zonas?provincia=${encodeURIComponent(selProvincia)}&municipio=${encodeURIComponent(selMunicipio)}`, { withCredentials: true });
                setZonas(r.data);
                setSelZona(v);
                await persistZona(v);
                toast.success(`Zona "${v}" añadida`);
              } catch { toast.error('Error al añadir'); }
            }}
            disabled={readOnly || !selMunicipio}
            placeholder="Buscar o escribir zona..."
            allowEmpty
            tid="val-zona"
          />
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Tipo inmueble<HelpHint text="Tipología del bien (vivienda, casa, local...). Filtra los comparables de precio/m² por tipo similar." /></label>
            <select className="input-base text-sm" value={selTipo} onChange={e => setSelTipo(e.target.value)} disabled={readOnly} data-testid="val-tipo-inmueble">
              <option value="vivienda">Vivienda</option>
              <option value="casa">Casa</option>
              <option value="pareada">Pareada</option>
              <option value="casa_aislada">Casa aislada</option>
              <option value="local">Local comercial</option>
              <option value="terreno">Terreno</option>
              <option value="parking">Parking</option>
              {(() => {
                const knownTokens = new Set([
                  'vivienda','casa','pareada','casa_aislada','local','terreno','parking',
                  'local comercial','casa aislada',
                ]);
                const seen = new Set();
                return tipos.filter(t => {
                  const k = String(t || '').toLowerCase().trim();
                  if (!k || knownTokens.has(k) || seen.has(k)) return false;
                  seen.add(k);
                  return true;
                }).map(t => <option key={t} value={t}>{t}</option>);
              })()}
            </select>
          </div>
        </div>

        {/* Base results */}
        {requiresManualValuation(selTipo) ? (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="val-requires-manual">
            <p className="text-sm text-amber-800 font-medium">
              Banco de España no recomienda realizar valoraciones automáticas de este tipo de activos ({selTipo}).
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Solicite <strong>Valoración IA</strong> en esta misma página si lo necesita, o introduzca el valor en el campo <strong>Ajuste manual</strong> del bloque Resultado.
            </p>
          </div>
        ) : (
        <>
        {/* --- Valor 1 (Idealista) --- */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor 1 · Idealista</span>
          <span className="h-px flex-1 bg-slate-200"></span>
        </div>
        <div className="grid md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="val-base-idealista">
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Metros</p>
            <p className="text-lg font-bold text-slate-800">{metros || '-'} m2</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Precio m2 base</p>
            <p className="text-lg font-bold text-slate-800">
              {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : precioBase ? `${precioBase.toLocaleString('es-ES')} €/m2` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Nivel fuente</p>
            {nivelFuente ? (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${NIVEL_BADGE[nivelFuente] || 'bg-slate-100 text-slate-600'}`} data-testid={`nivel-badge-idealista-${nivelFuente}`}>{nivelFuente}</span>
            ) : <p className="text-sm text-slate-400">-</p>}
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Valor salida base</p>
            <p className="text-xl font-bold text-slate-800 italic">{metros && precioBase ? cur(metros * precioBase) : '-'}</p>
          </div>
        </div>

        {/* --- Valor 2 (Fotocasa) --- */}
        <div className="flex items-center gap-2 mt-4 mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Valor 2 · Fotocasa</span>
          <span className="h-px flex-1 bg-slate-200"></span>
        </div>
        <div className="grid md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="val-base-fotocasa">
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Metros</p>
            <p className="text-lg font-bold text-slate-800">{metros || '-'} m2</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Precio m2 base</p>
            <p className="text-lg font-bold text-slate-800">
              {lookupLoadingFotocasa ? <Loader2 className="w-4 h-4 animate-spin inline" /> : precioBaseFotocasa ? `${precioBaseFotocasa.toLocaleString('es-ES')} €/m2` : '-'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Nivel fuente</p>
            {nivelFuenteFotocasa ? (
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${NIVEL_BADGE[nivelFuenteFotocasa] || 'bg-slate-100 text-slate-600'}`} data-testid={`nivel-badge-fotocasa-${nivelFuenteFotocasa}`}>{nivelFuenteFotocasa}</span>
            ) : <p className="text-sm text-slate-400">-</p>}
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase mb-1">Valor salida base</p>
            <p className="text-xl font-bold text-slate-800 italic">{metros && precioBaseFotocasa ? cur(metros * precioBaseFotocasa) : '-'}</p>
          </div>
        </div>
        </>
        )}

        {/* Save button — persists valor_salida_base + recalculates ROI rápido + score */}
        {!readOnly && !requiresManualValuation(selTipo) && (
          <div className="mt-4 flex items-center justify-end gap-3">
            <p className="text-xs text-slate-500 text-right">
              Si acabas de actualizar el precio €/m² del municipio/zona, pulsa <strong>Guardar</strong> para
              refrescar el valor de salida y recalcular el ROI rápido del activo.
            </p>
            <button
              onClick={handleSaveValorBase}
              disabled={savingValorBase}
              className="btn-primary text-sm flex items-center gap-1"
              data-testid="val-base-save-btn"
            >
              {savingValorBase ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {savingValorBase ? 'Guardando...' : 'Guardar Valor Base'}
            </button>
          </div>
        )}

        {/* Fiabilidad warnings */}
        {nivelFuente === 'provincia' && precioBase ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="warning-provincia">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed">
              <strong>Fiabilidad baja:</strong> no hay datos de precio a nivel de barrio ni de municipio, por lo que el valor estimado se calcula con el precio medio de la <strong>provincia</strong>. Es una aproximación orientativa, no una valoración precisa. Introduce un precio específico en <em>Parámetros · Base de Precios</em> para mejorar la estimación.
            </p>
          </div>
        ) : nivelFuente === 'municipio' && precioBase ? (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800" data-testid="warning-municipio">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs leading-relaxed">
              <strong>Fiabilidad media:</strong> se está usando el precio medio del municipio. Si conoces el precio del barrio/zona específicos, añádelo en <em>Parámetros · Base de Precios</em> para una valoración más precisa.
            </p>
          </div>
        ) : null}

        {/* Staleness warning: precio base was last updated >180 days ago */}
        {(() => {
          const at = asset?.precio_m2_base_at;
          if (!at || !precioBase) return null;
          try {
            const then = new Date(at);
            if (isNaN(then.getTime())) return null;
            const days = Math.floor((Date.now() - then.getTime()) / (1000 * 60 * 60 * 24));
            if (days <= 180) return null;
            const fecha = then.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
            return (
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="warning-stale">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p className="text-xs leading-relaxed">
                  <strong>Dato desactualizado ({days} días):</strong> el precio m² base se actualizó por última vez el {fecha}. Te recomendamos refrescarlo desde <em>Parámetros · Base de Precios → botón Idealista</em> para que la valoración refleje el mercado actual.
                </p>
              </div>
            );
          } catch { return null; }
        })()}
      </div>

      {/* ===== NIVEL 2: VALOR INDIVIDUAL (adjustments) ===== */}
      <div className="card-base p-6">
        <h3 className="heading-3 flex items-center gap-2 mb-2">
          <Calculator className="w-5 h-5 text-emerald-600" />
          Nivel 2: Valor Individual (ajustado)
        </h3>
        <p className="text-xs text-slate-400 mb-3">Precio m2 base ajustado por caracteristicas del inmueble</p>

        {/* Selector de fuente V1/V2/Media (Opción 4A: siempre visible, Media deshabilitada si falta uno) */}
        {!requiresManualValuation(selTipo) && (
          <div className="mb-5 p-3 rounded-lg border border-emerald-100 bg-emerald-50">
            <p className="text-xs font-semibold text-emerald-800 uppercase tracking-wide mb-2">Fuente del precio m² base</p>
            <div className="flex flex-wrap gap-2">
              {(() => {
                const hasV1 = !!(precioBase && precioBase > 0);
                const hasV2 = !!(precioBaseFotocasa && precioBaseFotocasa > 0);
                const opts = [
                  { key: 'idealista', label: 'Valor 1 · Idealista', enabled: hasV1, hint: hasV1 ? `${precioBase.toLocaleString('es-ES')} €/m²` : 'sin datos' },
                  { key: 'fotocasa', label: 'Valor 2 · Fotocasa', enabled: hasV2, hint: hasV2 ? `${precioBaseFotocasa.toLocaleString('es-ES')} €/m²` : 'sin datos' },
                  { key: 'media', label: 'Media (V1+V2)/2', enabled: hasV1 && hasV2, hint: (hasV1 && hasV2) ? `${(((precioBase + precioBaseFotocasa) / 2)).toLocaleString('es-ES')} €/m²` : 'necesita V1 y V2' },
                ];
                return opts.map(o => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      if (!o.enabled || readOnly) return;
                      setSourceChoice(o.key);
                      // Al pulsar la fuente, el precio m² individual y el valor individual (previo)
                      // se recalculan siempre — el usuario está eligiendo explícitamente la base.
                      let eff = 0;
                      if (o.key === 'idealista') eff = precioBase || 0;
                      else if (o.key === 'fotocasa') eff = precioBaseFotocasa || 0;
                      else if (o.key === 'media' && precioBase && precioBaseFotocasa) eff = (precioBase + precioBaseFotocasa) / 2;
                      if (eff) setForm(prev => ({ ...prev, precio_m2_zona: eff }));
                    }}
                    disabled={!o.enabled || readOnly}
                    className={`px-3 py-2 rounded-md border text-xs font-medium transition ${
                      sourceChoice === o.key
                        ? 'border-emerald-500 bg-emerald-500 text-white shadow-sm'
                        : o.enabled
                          ? 'border-slate-300 bg-white text-slate-700 hover:border-emerald-400'
                          : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid={`val-source-${o.key}`}
                    title={o.hint}
                  >
                    {o.label}
                    <span className="ml-2 text-[10px] opacity-80">{o.hint}</span>
                  </button>
                ));
              })()}
            </div>
            <p className="text-[10px] text-emerald-700 mt-2">
              La fuente elegida se guarda en el activo y se respeta en los recálculos masivos.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5 mb-5">
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Metros (m2)<HelpHint text="Superficie construida del inmueble. Base para calcular el valor individual (metros × precio/m²)." /></label>
            <input type="number" className={`input-base ${errors.metros ? 'input-error' : ''}`} value={form.metros || ''} onChange={e => set('metros', parseFloat(e.target.value) || 0)} disabled={readOnly} data-testid="val-metros" />
            {errors.metros && <p className="text-xs text-red-500 font-medium mt-1">Campo obligatorio</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Precio m2 individual<HelpHint text="Precio por metro cuadrado aplicado a este activo. Puede venir de Idealista (V1), Fotocasa (V2) o de la media." /></label>
            <input type="number" className={`input-base ${errors.precio_m2_zona ? 'input-error' : ''}`} value={form.precio_m2_zona || ''} onChange={e => set('precio_m2_zona', parseFloat(e.target.value) || 0)} disabled={readOnly} data-testid="val-precio-m2" />
            {errors.precio_m2_zona && <p className="text-xs text-red-500 font-medium mt-1">Campo obligatorio</p>}
            {(() => {
              // Valor "restaurable" según la fuente elegida
              let effective = 0;
              let label = '';
              if (sourceChoice === 'idealista' && precioBase) { effective = precioBase; label = 'V1'; }
              else if (sourceChoice === 'fotocasa' && precioBaseFotocasa) { effective = precioBaseFotocasa; label = 'V2'; }
              else if (sourceChoice === 'media' && precioBase && precioBaseFotocasa) { effective = (precioBase + precioBaseFotocasa) / 2; label = 'Media'; }
              else if (precioBase) { effective = precioBase; label = 'V1'; }
              if (!effective || form.precio_m2_zona === effective) return null;
              return (
                <p className="text-xs text-slate-400 mt-1">
                  Base {label}: {effective.toLocaleString('es-ES', { maximumFractionDigits: 2 })} €/m2
                  {!readOnly && <button onClick={() => set('precio_m2_zona', effective)} className="text-blue-500 hover:underline ml-2">restaurar</button>}
                </p>
              );
            })()}
          </div>
          <div className="space-y-1">
            <label className="text-sm text-slate-600 font-medium">Valor individual (previo)<HelpHint text="Producto de m² × precio/m². Se usa como base antes de aplicar ajustes (características, IA, override manual)." /></label>
            <p className="text-2xl font-bold text-slate-800 py-1">{metros && form.precio_m2_zona ? cur(metros * form.precio_m2_zona) : '-'}</p>
          </div>
        </div>

        {/* ===== Características del inmueble (Nivel 1 — estructura común VENTA/ALQUILER) ===== */}
        {/* NOTA: Los campos subjetivos legacy (exterior_luminosidad, orientacion,
            posesion_val, vistas_ubicacion_micro, distribucion) y los "Extras"
            (terraza/balcon/jardin/piscina/garaje/trastero) NO se renderizan aquí.
            Sus valores se conservan en `asset.valoracion_salida` y en el form
            local para no perder datos históricos, pero se envían neutros al
            motor de cálculo en `handleCalculate` para que no impacten en el
            resultado nuevo. Piscina/Garaje se gestionan ahora desde el bloque
            común de features (property_features.pool / property_features.garage). */}
        <PropertyFeaturesBlock
          asset={asset}
          assetId={assetId}
          readOnly={readOnly}
          titleVariant="heading"
          showInfo={false}
          onSaved={handleFeatureSaved}
          onAssetUpdate={onAssetUpdated}
        />

        {!readOnly && (
          <div className="mt-5 pt-4 border-t border-slate-200">
            <button onClick={handleCalculate} disabled={calculating} className="btn-primary flex items-center gap-2" data-testid="val-calculate-btn">
              {calculating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              {calculating ? 'Calculando...' : 'Calcular y guardar valoracion individual'}
            </button>
          </div>
        )}
      </div>

      {/* ===== RESULTS ===== */}
      {result && (
        <div className="card-base p-6" data-testid="val-result">
          <h3 className="heading-3 mb-5">Resultado</h3>

          {/* Comparison: Base vs Individual */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Nivel 1: Valor Base</p>
              <p className="text-xl font-bold text-slate-600 italic">{valorBase > 0 ? cur(valorBase) : '-'}</p>
              <p className="text-xs text-slate-400 mt-1">{metros} m2 x {precioBase ? `${precioBase.toLocaleString('es-ES')} €/m2` : '-'} ({nivelFuente || '-'})</p>
            </div>
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-xs text-emerald-600 uppercase tracking-wide mb-2">Nivel 2: Valor Individual</p>
              <p className="text-2xl font-bold text-emerald-700" data-testid="val-valor-final">{cur(result.valor_salida_base)}</p>
              <p className="text-xs text-slate-400 mt-1">Base {cur(result.valor_base)} {result.ajuste_total_pct >= 0 ? '+' : ''}{result.ajuste_total_pct}% ajuste</p>

              {!readOnly && (
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <label className="text-xs text-emerald-700 font-medium uppercase tracking-wide flex items-center gap-2">
                    Ajuste manual <span className="text-[10px] text-emerald-500 lowercase tracking-normal">(opcional)</span>
                  </label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="number"
                      step="1"
                      placeholder={Math.round(result.valor_salida_base).toString()}
                      className="input-base text-sm flex-1"
                      value={manualOverride}
                      onChange={e => setManualOverride(e.target.value)}
                      data-testid="val-manual-override"
                    />
                    <span className="text-sm text-slate-500">€</span>
                    {manualOverride !== '' && (
                      <button
                        onClick={() => setManualOverride('')}
                        className="text-xs text-slate-500 hover:text-red-500"
                        data-testid="val-manual-override-clear"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Si rellenas un valor, éste se guardará como <strong>Valor de salida</strong> en lugar del calculado ({cur(result.valor_salida_base)}).
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Detail table */}
          {result.ajustes?.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50">
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Concepto</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Valor</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Ajuste</th>
                </tr></thead>
                <tbody>
                  {result.ajustes.map((a, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-slate-700">{a.campo}</td>
                      <td className="px-4 py-2 text-slate-500">{a.valor}</td>
                      <td className={`px-4 py-2 text-right font-medium ${a.ajuste > 0 ? 'text-emerald-600' : a.ajuste < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                        {a.ajuste > 0 ? '+' : ''}{a.ajuste}%
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    <td className="px-4 py-2 text-slate-800" colSpan="2">Total ajuste</td>
                    <td className={`px-4 py-2 text-right ${result.ajuste_total_pct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      {result.ajuste_total_pct >= 0 ? '+' : ''}{result.ajuste_total_pct}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* AI Review */}
          {!readOnly && (
            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Revision IA (opcional)</p>
                  <p className="text-xs text-slate-400">La IA puede sugerir la corrección porcentual que considere justificada (sin límite).</p>
                </div>
                <button onClick={handleAiReview} disabled={aiLoading} className="btn-secondary text-sm flex items-center gap-2" data-testid="val-ai-review-btn">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {aiLoading ? 'Analizando...' : 'Consultar IA'}
                </button>
              </div>
              {aiReview && (
                <div className={`p-4 rounded-lg border ${aiReview.correccion_pct === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="val-ai-result">
                  <div className="flex items-start gap-3">
                    <Check className={`w-5 h-5 mt-0.5 ${aiReview.correccion_pct === 0 ? 'text-emerald-600' : 'text-amber-600'}`} />
                    <div className="flex-1">
                      {aiReview.correccion_pct === 0 ? (
                        <p className="text-sm font-medium text-emerald-700">Ajustes razonables. Sin correccion sugerida.</p>
                      ) : (
                        <p className="text-sm font-medium text-amber-700">
                          Correccion sugerida: {aiReview.correccion_pct > 0 ? '+' : ''}{aiReview.correccion_pct}% &rarr; {cur(aiReview.valor_con_correccion)}
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mt-1">{aiReview.razon}</p>
                      {aiReview.correccion_pct !== 0 && !aiApplied && (
                        <button
                          onClick={applyAiCorrection}
                          className="mt-3 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
                          data-testid="val-ai-apply-btn"
                        >
                          Aplicar esta corrección al Valor Individual
                        </button>
                      )}
                      {aiApplied && (
                        <p className="mt-2 text-xs text-emerald-600 font-medium">✓ Corrección aplicada</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Save button */}
          {!readOnly && (
            <div className="mt-5 pt-5 border-t border-slate-200 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Guardar crea un registro en el histórico con tu nombre y la fecha actual.
              </p>
              <button
                onClick={handleSaveValoracion}
                disabled={saving}
                className="btn-primary flex items-center gap-2"
                data-testid="val-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {saving ? 'Guardando...' : 'Guardar valoración'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== AJUSTE MANUAL DIRECTO (siempre visible) =====
          Permite al usuario fijar el Valor de Salida sin pasar por los Niveles 1+2.
          Pensado para activos donde no hay datos suficientes para el cálculo
          automático (típicamente CRÉDITOS) o donde el usuario ya conoce el valor. */}
      {!readOnly && (
        <div className="card-base p-6" data-testid="val-salida-manual">
          <h3 className="heading-3 flex items-center gap-2 mb-2">
            <Calculator className="w-5 h-5 text-amber-600" />
            Valor de Salida — Ajuste manual directo
          </h3>
          <p className="text-xs text-slate-400 mb-5">
            Si conoces el valor de salida directamente y no quieres pasar por los Niveles 1+2 (por ejemplo en créditos sin datos suficientes), introdúcelo aquí. Se guardará como <strong>Valor de Salida</strong> del activo y aparecerá en el histórico.
          </p>
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <label className="text-sm text-slate-600 font-medium mb-1 block">Valor de salida (€)<HelpHint text="Precio de salida a mercado. Sobrescribe cualquier cálculo automático de valoración. Base para el ROI y las ofertas." /></label>
              <input
                type="number"
                step="1"
                placeholder="Ej. 85000"
                className="input-base"
                value={valorSalidaManual}
                onChange={(e) => setValorSalidaManual(e.target.value)}
                data-testid="val-salida-manual-input"
              />
            </div>
            <button
              onClick={handleSaveValorSalidaManual}
              disabled={savingManual || !valorSalidaManual}
              className="btn-primary flex items-center gap-2"
              data-testid="val-salida-manual-save"
            >
              {savingManual ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {savingManual ? 'Guardando...' : 'Guardar valor'}
            </button>
          </div>
          {asset?.valor_salida_individual && asset.valor_salida_individual > 0 && (
            <p className="text-xs text-emerald-600 mt-3">
              ✓ Hay un ajuste manual aplicado actualmente: <strong>{cur(asset.valor_salida_individual)}</strong>. Modifica el valor o vacía el campo y guarda para reemplazarlo.
            </p>
          )}
        </div>
      )}

      {/* ===== HISTORIAL DE VALORACIONES ===== */}
      {history.length > 0 && (
        <div className="card-base p-6" data-testid="valoracion-history">
          <h3 className="heading-3 flex items-center gap-2 mb-4">
            <Calculator className="w-5 h-5 text-slate-500" />
            Histórico de valoraciones ({history.length})
          </h3>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Fecha</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase">Usuario</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Valor base</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Ajuste</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Corrección IA</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-slate-500 uppercase">Valor final</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => {
                  const d = new Date(h.fecha);
                  const fechaStr = d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  return (
                    <tr key={h.id} className="border-t border-slate-100" data-testid={`hist-row-${h.id}`}>
                      <td className="px-3 py-2.5 text-slate-700">{fechaStr}</td>
                      <td className="px-3 py-2.5 text-slate-700">{h.usuario || '-'}</td>
                      <td className="px-3 py-2.5 text-right text-slate-500">{h.valor_base ? cur(h.valor_base) : '-'}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${(h.ajuste_total_pct ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {h.ajuste_total_pct != null ? `${h.ajuste_total_pct >= 0 ? '+' : ''}${h.ajuste_total_pct}%` : '-'}
                      </td>
                      <td className={`px-3 py-2.5 text-right font-medium ${(h.correccion_ia_pct ?? 0) !== 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {h.correccion_ia_pct != null && h.correccion_ia_pct !== 0 ? `${h.correccion_ia_pct >= 0 ? '+' : ''}${h.correccion_ia_pct}%` : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-emerald-700">{cur(h.valor_final)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const Sel = ({ label, help, value, options, onChange, disabled, tid }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-600 font-medium">{label}{help && <HelpHint text={help} />}</label>
    <select className="input-base" value={value} onChange={e => onChange(e.target.value)} disabled={disabled} data-testid={tid}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const ComboField = ({ label, help, value, options, onChange, onAdd, disabled, placeholder, allowEmpty, tid }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const isNew = search.trim() && !options.some(o => o.toLowerCase() === search.trim().toLowerCase());

  return (
    <div className="space-y-1 relative" ref={ref} data-testid={tid}>
      <label className="text-sm text-slate-600 font-medium">{label}{help && <HelpHint text={help} />}</label>
      <input
        type="text"
        className="input-base text-sm"
        value={open ? search : value}
        onFocus={() => { setOpen(true); setSearch(value); }}
        onChange={e => setSearch(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto" style={{ minWidth: '200px' }}>
          {allowEmpty && (
            <button onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50">
              Sin seleccionar
            </button>
          )}
          {filtered.map(o => (
            <button key={o} onClick={() => { onChange(o); setOpen(false); setSearch(''); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${o === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700'}`}>
              {o}
            </button>
          ))}
          {isNew && (
            <button onClick={() => { onAdd(search.trim()); setOpen(false); setSearch(''); }}
              className="w-full text-left px-3 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50 border-t border-slate-100 flex items-center gap-1">
              + Añadir "{search.trim()}"
            </button>
          )}
          {filtered.length === 0 && !isNew && (
            <p className="px-3 py-2 text-xs text-slate-400">Sin resultados</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ValoracionSalidaTab;
